import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Search, Upload, X, Tag, Edit2, Trash2, Library, Image as ImageIcon, AlertTriangle } from "lucide-react";
import { errMsg } from "@/lib/errors";

export default function PhotoLibraryTab() {
  const [items, setItems] = useState([]);
  const [tags, setTags] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [activeTag, setActiveTag] = useState("");
  const [activeCategory, setActiveCategory] = useState("");
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // photo doc
  const [deleting, setDeleting] = useState(null); // {photo, used_in?}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (activeTag) params.tag = activeTag;
      if (activeCategory) params.category = activeCategory;
      const [libRes, tagsRes] = await Promise.all([
        api.get("/admin/photo-library", { params }),
        api.get("/admin/photo-library/tags"),
      ]);
      setItems(libRes.data?.items || []);
      setTags(tagsRes.data?.tags || []);
      setCategories(tagsRes.data?.categories || []);
    } catch (e) {
      toast.error("Errore nel caricamento dell'archivio");
    } finally {
      setLoading(false);
    }
  }, [q, activeTag, activeCategory]);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (files) => {
    const arr = Array.from(files || []);
    if (arr.length === 0) return;
    let count = 0;
    for (const file of arr) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        await api.post("/admin/upload-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
        count++;
      } catch (err) {
        toast.error(`${file.name}: ${errMsg(err, "errore")}`);
      }
    }
    if (count > 0) {
      toast.success(`${count} foto caricate`);
      load();
    }
  };

  const handleDelete = async (force = false) => {
    if (!deleting) return;
    try {
      const res = await api.delete(`/admin/photo-library/${deleting.photo.id}`, { params: { force } });
      if (res.data?.deleted) {
        toast.success("Foto eliminata");
        setDeleting(null);
        load();
      } else if (res.data?.in_use) {
        // Show usage and ask for confirmation
        setDeleting({ photo: deleting.photo, used_in: res.data.used_in });
      }
    } catch (e) {
      toast.error(errMsg(e, "Errore eliminazione"));
    }
  };

  const totalSize = useMemo(() => items.reduce((s, p) => s + (p.size || 0), 0), [items]);

  return (
    <section className="space-y-5" data-testid="photo-library-tab">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-[#1E3A5F] flex items-center gap-2">
            <Library className="w-6 h-6 text-[#AC5D45]" /> Archivio Foto
          </h2>
          <p className="text-sm text-[#635F59] mt-0.5">
            Carica una volta, riusa ovunque. Aggiungi tag per ritrovarle in fretta.
          </p>
        </div>
        <label className="flex items-center gap-2 h-10 px-4 rounded-md cursor-pointer bg-[#AC5D45] hover:bg-[#8A4936] text-white transition text-sm font-semibold" data-testid="photo-library-upload-button">
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
            multiple
            className="hidden"
            onChange={(e) => { const f = Array.from(e.target.files || []); e.target.value = ""; handleUpload(f); }}
          />
          <Upload className="w-4 h-4" />
          Carica foto
        </label>
      </header>

      <div className="bg-white rounded-xl border border-[#E8E2D9] p-4 space-y-3">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#635F59]" />
            <Input
              placeholder="Cerca per titolo, tag o nome file…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-8"
              data-testid="photo-library-search"
            />
          </div>
          {(activeTag || activeCategory) && (
            <Button variant="outline" size="sm" onClick={() => { setActiveTag(""); setActiveCategory(""); }}>
              <X className="w-3 h-3 mr-1" /> Reset filtri
            </Button>
          )}
          <span className="text-xs text-[#635F59]">
            {items.length} foto · {(totalSize / (1024 * 1024)).toFixed(1)} MB
          </span>
        </div>

        {categories.length > 0 && (
          <div className="flex flex-wrap gap-1.5 items-center">
            <span className="text-[11px] uppercase tracking-wide text-[#635F59] font-semibold mr-1">Categorie</span>
            {categories.map((c) => (
              <button
                key={`cat-${c}`}
                type="button"
                data-testid={`photo-library-cat-${c}`}
                onClick={() => setActiveCategory(activeCategory === c ? "" : c)}
                className={`text-xs px-2 py-0.5 rounded-full border ${activeCategory === c ? "bg-[#1E3A5F] text-white border-[#1E3A5F]" : "bg-white border-[#E8E2D9] text-[#1E3A5F] hover:border-[#AC5D45]"}`}
              >
                {c}
              </button>
            ))}
          </div>
        )}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 items-center">
            <span className="text-[11px] uppercase tracking-wide text-[#635F59] font-semibold mr-1">Tag</span>
            {tags.slice(0, 50).map((t) => (
              <button
                key={`tag-${t}`}
                type="button"
                data-testid={`photo-library-tag-${t}`}
                onClick={() => setActiveTag(activeTag === t ? "" : t)}
                className={`text-xs px-2 py-0.5 rounded-full border ${activeTag === t ? "bg-[#AC5D45] text-white border-[#AC5D45]" : "bg-white border-[#E8E2D9] text-[#1E3A5F] hover:border-[#AC5D45]"}`}
              >
                #{t}
              </button>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <p className="text-center text-[#635F59] py-12">Caricamento…</p>
      ) : items.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-dashed border-[#E8E2D9]">
          <ImageIcon className="w-12 h-12 mx-auto text-[#E8E2D9] mb-2" />
          <p className="text-[#635F59]">Nessuna foto trovata.</p>
          <p className="text-xs text-[#635F59] mt-1">Carica le tue prime foto col pulsante in alto a destra.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {items.map((p) => (
            <article key={p.id} className="bg-white rounded-lg border border-[#E8E2D9] overflow-hidden flex flex-col group" data-testid={`photo-library-item-${p.id}`}>
              <div className="relative aspect-square bg-[#FDFBF7]">
                <img src={p.url} alt={p.title || ""} className="w-full h-full object-cover" loading="lazy" />
                {p.category && (
                  <span className="absolute top-1 left-1 bg-black/60 text-white text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wide">
                    {p.category}
                  </span>
                )}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-1.5">
                  <button
                    onClick={() => setEditing(p)}
                    className="bg-white/95 hover:bg-white rounded p-1.5"
                    title="Modifica"
                    data-testid={`photo-library-edit-${p.id}`}
                  >
                    <Edit2 className="w-3.5 h-3.5 text-[#1E3A5F]" />
                  </button>
                  <button
                    onClick={() => setDeleting({ photo: p })}
                    className="bg-rose-600 hover:bg-rose-700 text-white rounded p-1.5"
                    title="Elimina"
                    data-testid={`photo-library-delete-${p.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <div className="p-2 text-xs space-y-1">
                <div className="font-semibold text-[#1E3A5F] line-clamp-1" title={p.title || p.original_filename}>
                  {p.title || p.original_filename || p.filename}
                </div>
                {p.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {p.tags.slice(0, 4).map((t) => (
                      <span key={t} className="text-[9px] bg-[#FDFBF7] border border-[#E8E2D9] text-[#635F59] px-1.5 rounded">
                        #{t}
                      </span>
                    ))}
                    {p.tags.length > 4 && <span className="text-[9px] text-[#635F59]">+{p.tags.length - 4}</span>}
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      )}

      {editing && (
        <EditPhotoDialog
          photo={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
          categories={categories}
        />
      )}

      {deleting && (
        <Dialog open onOpenChange={(v) => !v && setDeleting(null)}>
          <DialogContent data-testid="photo-library-delete-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-rose-600" />
                Elimina foto
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <img src={deleting.photo.url} alt="" className="w-32 h-32 object-cover rounded mx-auto" />
              {deleting.used_in?.length > 0 ? (
                <>
                  <p className="text-sm text-rose-700 font-semibold">
                    Questa foto è ancora in uso:
                  </p>
                  <ul className="text-xs text-[#635F59] list-disc pl-5 space-y-0.5">
                    {deleting.used_in.map((u, i) => (
                      <li key={`${u.type}-${u.label}-${i}`}>{u.type}: {u.label}</li>
                    ))}
                  </ul>
                  <p className="text-xs text-[#635F59]">
                    Eliminandola apparirà uno spazio vuoto in queste sezioni. Procedere comunque?
                  </p>
                </>
              ) : (
                <p className="text-sm text-[#635F59]">L'eliminazione è definitiva. Continuare?</p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleting(null)} data-testid="photo-library-delete-cancel">Annulla</Button>
              <Button
                onClick={() => handleDelete(deleting.used_in?.length > 0)}
                className="bg-rose-600 hover:bg-rose-700 text-white"
                data-testid="photo-library-delete-confirm"
              >
                <Trash2 className="w-4 h-4 mr-1" /> Elimina
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </section>
  );
}

function EditPhotoDialog({ photo, onClose, onSaved, categories }) {
  const [title, setTitle] = useState(photo.title || "");
  const [category, setCategory] = useState(photo.category || "");
  const [tagsInput, setTagsInput] = useState((photo.tags || []).join(", "));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const tagsArr = tagsInput.split(",").map((t) => t.trim()).filter(Boolean);
      await api.patch(`/admin/photo-library/${photo.id}`, {
        title: title.trim() || null,
        category: category.trim() || null,
        tags: tagsArr,
      });
      toast.success("Foto aggiornata");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e, "Errore"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="photo-library-edit-dialog">
        <DialogHeader>
          <DialogTitle>Modifica foto</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <img src={photo.url} alt="" className="w-full h-48 object-cover rounded" />
          <div>
            <Label>Titolo</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1" data-testid="photo-library-edit-title" />
          </div>
          <div>
            <Label>Categoria</Label>
            <Input value={category} onChange={(e) => setCategory(e.target.value)} list="cat-list-edit" className="mt-1" data-testid="photo-library-edit-category" placeholder="es. rooms, gallery, services…" />
            <datalist id="cat-list-edit">
              {categories.map((c) => <option key={c} value={c} />)}
            </datalist>
          </div>
          <div>
            <Label>Tag (separati da virgola)</Label>
            <Input value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} className="mt-1" data-testid="photo-library-edit-tags" placeholder="es. mare, vista, tramonto" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={saving} className="bg-[#AC5D45] hover:bg-[#8A4936] text-white" data-testid="photo-library-edit-save">
            {saving ? "Salvo…" : "Salva"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
